import { createAsyncThunk } from '@reduxjs/toolkit';
import { apiCallWithRetry, client } from '@shared/api/client';
import { fromZonedDay, toZonedDay } from '@shared/lib/zonedDay';

// Fetch single order
export const fetchOrder = createAsyncThunk(
  'order/fetchOrder',
  async ({ reorderUuid, accountUuid }, { rejectWithValue }) => {
    const apiCall = async () => {
      return await client.get(`/accounts/${accountUuid}/orders/${reorderUuid}`);
    };

    try {
      const response = await apiCallWithRetry(apiCall, 3, 1000);

      if (response.ok) {
        return response.data;
      }

      return rejectWithValue({
        error: response.data?.message || 'Failed to load order',
        status: response.status,
        validationErrors: response.data?.validationErrors || null,
      });
    } catch (error) {
      return rejectWithValue({
        error: typeof error === 'string' ? error : 'Network error',
        status: null,
      });
    }
  }
);

// Update existing order
export const updateOrder = createAsyncThunk(
  'order/updateOrder',
  async (
    { accountUuid, orderUuid, formValues, originalOrder, userUuid, metadata, timeZone },
    { rejectWithValue }
  ) => {
    const { compare } = await import('fast-json-patch');
    const { default: isEqual } = await import('lodash/isEqual');

    const {
      slitLowDose,
      slitLowDoseQuantity,
      slitMaintenance,
      slitMaintenanceQuantity,
      ...values
    } = formValues;

    const formattedValues = {
      ...values,
      slitLowDoseQuantity:
        slitLowDose && slitLowDoseQuantity ? slitLowDoseQuantity : 0,
      slitMaintenanceQuantity:
        slitMaintenance && slitMaintenanceQuantity
          ? slitMaintenanceQuantity
          : 0,
    };

    const orderCopy = { ...originalOrder.data };
    delete formattedValues.scitAllergens; // will be patched manually
    delete formattedValues.expectedDeliveryDate; // will be patched manually
    delete formattedValues.slitLowDose; // internal usage
    delete formattedValues.slitMaintenance; // internal usage
    delete orderCopy.scitAllergens;
    delete orderCopy.expectedDeliveryDate;
    delete orderCopy.slitLowDose;
    delete orderCopy.slitMaintenance;

    // Diff ONLY the fields the form owns. The GET payload also carries
    // server-side fields the form never edits (uuid, tags, status,
    // orderDate, patientUUID, accountUUID, ...); comparing against the
    // full object emitted a bogus `remove` op for every one of them
    // and the backend rejected the whole patch. Restricting the
    // baseline to the form's keys yields replace/add ops for edited
    // fields only.
    const baseline = {};
    Object.keys(formattedValues).forEach(key => {
      if (key in orderCopy) baseline[key] = orderCopy[key];
    });
    const patches = compare(baseline, formattedValues);
    // create a patch for an array manually
    if (!isEqual(originalOrder.data.scitAllergens, formValues.scitAllergens)) {
      patches.push({
        op: 'replace',
        path: '/scitAllergens',
        value: formValues.scitAllergens,
      });
    }

    // `expectedDeliveryDate` is edited as a calendar day but stored as
    // local midnight of that day in the ACCOUNT's time zone. Comparing
    // the two representations directly would emit a replace op on
    // every save, so the day is what gets diffed and the UTC instant
    // is built only when it actually changed.
    const originalDeliveryDay = toZonedDay(originalOrder.data.expectedDeliveryDate, timeZone);
    if ((formValues.expectedDeliveryDate || '') !== originalDeliveryDay) {
      const value = fromZonedDay(formValues.expectedDeliveryDate, timeZone);
      if (value) {
        patches.push({ op: 'replace', path: '/expectedDeliveryDate', value });
      }
    }

    if (patches.length === 0) {
      // No changes to save
      return {
        data: originalOrder.data,
        originalOrder,
        patientName: metadata?.patientInfo?.name || '',
        noChanges: true,
      };
    }

    const apiCall = async () => {
      return await client.patch(`/accounts/${accountUuid}/orders/${orderUuid}`, patches);
    };

    try {
      const response = await apiCallWithRetry(apiCall, 3, 1000);
      if (response.ok) {
        return {
          data: response.data,
          originalOrder,
          patientName: metadata?.patientInfo?.name || '',
          originalFormValues: { slitLowDose, slitMaintenance },
        };
      } else {
        return rejectWithValue({
          status: response?.status,
          errors: response?.data?.validationErrors || {},
          correlationUUID: response?.data?.correlationUUID,
          userUUID: userUuid,
          resetSlitFields: { slitLowDose, slitMaintenance },
          ...response?.data,
        });
      }
    } catch (error) {
      return rejectWithValue({
        error: typeof error === 'string' ? error : 'Network error',
        status: null,
        errors: {},
        userUUID: userUuid,
        resetSlitFields: { slitLowDose, slitMaintenance },
      });
    }
  }
);


// Fetch create order metadata (patient info, providers, addresses)
export const fetchCreateOrderMetadata = createAsyncThunk(
  'order/fetchCreateOrderMetadata',
  async ({ accountUuid, patientUuid }, { rejectWithValue }) => {
    const apiCall = async () => {
      return await client.get(`/accounts/${accountUuid}/patients/${patientUuid}/create-order-metadata`);
    };

    try {
      const response = await apiCallWithRetry(apiCall, 3, 1000);

      if (response.ok) {
        return response.data;
      }

      return rejectWithValue({
        error: response.data?.message || 'Failed to load order metadata',
        status: response.status,
      });
    } catch (error) {
      return rejectWithValue({
        error: typeof error === 'string' ? error : 'Network error',
        status: null,
      });
    }
  }
);

// Check immunotherapy report permission
export const checkImmunotherapyPermission = createAsyncThunk(
  'order/checkImmunotherapyPermission',
  async (accountUuid, { rejectWithValue }) => {
    const apiCall = async () => {
      return await client.get(`/accounts/${accountUuid}/account-permissions`);
    };

    try {
      const response = await apiCallWithRetry(apiCall, 3, 1000);

      if (response.ok) {
        return response.data?.data?.enabletImmunotherapyReport || false;
      }

      return rejectWithValue({
        error: response.data?.message || 'Failed to check permissions',
        status: response.status,
      });
    } catch (error) {
      return rejectWithValue({
        error: typeof error === 'string' ? error : 'Network error',
        status: null,
      });
    }
  }
);

// Submit order
export const submitOrder = createAsyncThunk(
  'order/submitOrder',
  async ({ formValues, accountUuid, patientUuid }, { rejectWithValue }) => {
    const {
      slitLowDose,
      slitLowDoseQuantity,
      slitMaintenance,
      slitMaintenanceQuantity,
      // The server computes the delivery date on create and ignores
      // whatever the payload carries; a reorder would otherwise send
      // the source order's day in the form's MM/DD/YYYY shape.
      expectedDeliveryDate,
      ...values
    } = formValues;

    const formattedValues = {
      ...values,
      slitLowDoseQuantity:
        slitLowDose && slitLowDoseQuantity ? slitLowDoseQuantity : 0,
      slitMaintenanceQuantity:
        slitMaintenance && slitMaintenanceQuantity
          ? slitMaintenanceQuantity
          : 0,
    };

    const apiCall = async () => {
      return await client.put(`/accounts/${accountUuid}/patients/${patientUuid}/orders`, formattedValues);
    };

    try {
      const response = await apiCallWithRetry(apiCall, 3, 1000);
      if (response.ok) {
        return response.data;
      } else {
        return rejectWithValue({
          status: response?.status,
          errors: response?.data?.validationErrors || {},
          correlationUUID: response?.data?.correlationUUID,
          originalFormValues: { slitLowDose, slitMaintenance },
          ...response?.data,
        });
      }
    } catch (error) {
      return rejectWithValue({
        error: typeof error === 'string' ? error : 'Network error',
        status: null,
        errors: {},
        originalFormValues: { slitLowDose, slitMaintenance },
      });
    }
  }
);

// Validate order (without saving)
export const validateOrder = createAsyncThunk(
  'order/validateOrder',
  async (orderData, { rejectWithValue }) => {
    const apiCall = async () => {
      return await client.post('/orders?validate-only=true', orderData);
    };

    try {
      const response = await apiCallWithRetry(apiCall, 3, 1000);

      if (response.ok) {
        return response.data;
      }

      return rejectWithValue({
        error: response.data?.message || 'Order validation failed',
        status: response.status,
        validationErrors: response.data?.validationErrors || null,
      });
    } catch (error) {
      return rejectWithValue({
        error: typeof error === 'string' ? error : 'Network error',
        status: null,
      });
    }
  }
);
